import { Button, Css, LoadingSkeleton, useTestIds } from "@homebound/beam";
import { useState } from "react";
import { FormattedDate } from "src/components";
import { Order } from "src/generated/graphqlTypes";
import { useCurrentUser } from "src/hooks/useCurrentUser";
import { useUserEventsQuery } from "src/routes/my-blueprint/activity-feed/ActivityFeedPage.generated";
import { generateRowData, getEventText } from "src/routes/my-blueprint/activity-feed/UserEventsTable";
import { queryResult } from "src/utils";

type UserEventsFeedProps = {
  /** The entities to show events for, i.e. a plan package plus its options. */
  parentIds: string[];
  parentType: string[];
  /** How many events to fetch, vs. how many to show before the "View All" toggle. */
  limit?: number;
  initialCount?: number;
};

/** A compact, reverse-chronological list of User Events. */
export function UserEventsFeed(props: UserEventsFeedProps) {
  const { parentIds, parentType, limit = 50, initialCount = 6 } = props;
  const tid = useTestIds({}, "userEventsFeed");
  const currentUser = useCurrentUser();
  const [expanded, setExpanded] = useState(false);
  const query = useUserEventsQuery({
    variables: { filter: { parent: parentIds, parentType }, limit, order: { createdAt: Order.Desc } },
  });

  return queryResult(query, {
    loading: () => <LoadingSkeleton rows={initialCount} size="sm" />,
    data: ({ userEvents }) => {
      const events = userEvents
        .map((event) => {
          const row = generateRowData(currentUser, event, false);
          return row && { id: event.id, row };
        })
        .compact();

      return events.isEmpty ? (
        <div css={Css.br8.py3.tac.$}>No recent activity.</div>
      ) : (
        <>
          <ul css={Css.listReset.if(expanded).maxhPx(500).overflowY("auto").$}>
            {(expanded ? events : events.slice(0, initialCount)).map(({ id, row }, i) => (
              <li key={id} css={Css.py2.if(i > 0).bt.bcGray200.$} {...tid.event}>
                <div css={Css.sm.gray900.$}>{getEventText(row, false, false)}</div>
                <div css={Css.xs2.gray700.$}>
                  <FormattedDate date={row.createdAt} dateFormatStyle="medium" timeFormatStyle="short" />
                </div>
              </li>
            ))}
          </ul>
          {events.length > initialCount && (
            <div css={Css.df.jcfe.$}>
              <Button
                variant="tertiary"
                label={expanded ? "View Less" : "View All"}
                onClick={() => setExpanded(!expanded)}
                {...tid.viewAll}
              />
            </div>
          )}
        </>
      );
    },
  });
}
